import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building2, Users, ShieldCheck, UserCog, Plus, ArrowLeft, LogOut,
  Search, Trash2, Layers, Activity, X,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import toast from "react-hot-toast";
import { backendApi } from "./api/backendApi";
import Invoices from "./Invoices";

const TENANT_COLORS = ["#0EA5A4", "#F59E0B", "#8B5CF6", "#3B82F6", "#EF4444", "#10B981", "#EC4899"];

const roleBadge = (role) => {
  const map = {
    admin: "bg-teal-100 text-teal-800",
    hr: "bg-teal-100 text-teal-800",
    manager: "bg-indigo-100 text-indigo-800",
    employee: "bg-slate-100 text-slate-700",
  };
  return map[role] || "bg-slate-100 text-slate-700";
};

// ── Small KPI card ──
const Kpi = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-2xl font-extrabold text-slate-800 leading-none">{value}</p>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-1">{label}</p>
    </div>
  </div>
);

export default function SuperAdminDashboard({ onLogout, userName }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("clients"); // 'clients' | 'invoices'
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [showAddTenant, setShowAddTenant] = useState(false);

  const { data: overview, isLoading } = useQuery({
    queryKey: ["sa-overview"],
    queryFn: () => backendApi.superadmin.overview(),
  });

  const totals = overview?.totals || {};
  const tenants = overview?.tenants || [];

  const chartData = tenants.map((t) => ({ name: t.name, users: t.user_count }));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src="/logo 2.jpg"
                  alt="WebIdeal Logo"
                  className="w-11 h-11 object-contain transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
          <div>
            <h1 className="text-base font-extrabold text-slate-800 leading-none">WebIdeal Platform Console</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Superadmin</span>
          </div>
        </div>
        <nav className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setActiveTab("clients")}
            className={`h-8 px-4 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
              activeTab === "clients" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Clients
          </button>
          <button
            onClick={() => { setActiveTab("invoices"); setSelectedTenantId(null); }}
            className={`h-8 px-4 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
              activeTab === "invoices" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Invoices
          </button>
        </nav>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-600">{userName || "Platform Owner"}</span>
          <button
            onClick={onLogout}
            className="h-9 px-4 rounded-xl text-xs font-bold uppercase inline-flex items-center gap-2 text-white bg-slate-800 hover:bg-slate-700"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {activeTab === "invoices" ? (
          <Invoices />
        ) : selectedTenantId ? (
          <TenantDetail
            tenantId={selectedTenantId}
            onBack={() => setSelectedTenantId(null)}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-extrabold text-slate-800">Clients Overview</h2>
                <p className="text-slate-500 text-sm">All companies (tenants) using your product</p>
              </div>
              <button
                onClick={() => setShowAddTenant(true)}
                className="h-11 px-5 rounded-xl text-sm font-bold inline-flex items-center gap-2 text-white bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="w-4 h-4" /> Onboard Client
              </button>
            </div>

            {isLoading ? (
              <p className="text-slate-400">Loading platform data…</p>
            ) : (
              <>
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                  <Kpi icon={Building2} label="Clients" value={totals.tenants ?? 0} color="bg-teal-500" />
                  <Kpi icon={Users} label="Total Users" value={totals.users ?? 0} color="bg-indigo-500" />
                  <Kpi icon={Activity} label="Active" value={totals.active_users ?? 0} color="bg-green-500" />
                  <Kpi icon={ShieldCheck} label="Admins" value={totals.admins ?? 0} color="bg-amber-500" />
                  <Kpi icon={UserCog} label="Managers" value={totals.managers ?? 0} color="bg-purple-500" />
                </div>

                {/* Chart */}
                <div className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100 mb-8">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
                    <Layers className="w-4 h-4" /> Users per Client
                  </h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="users" radius={[8, 8, 0, 0]}>
                        {chartData.map((_, i) => (
                          <Cell key={i} fill={TENANT_COLORS[i % TENANT_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Tenant cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {tenants.map((t, i) => (
                    <TenantCard
                      key={t.id}
                      tenant={t}
                      color={TENANT_COLORS[i % TENANT_COLORS.length]}
                      onOpen={() => setSelectedTenantId(t.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>

      {showAddTenant && (
        <AddTenantModal
          onClose={() => setShowAddTenant(false)}
          onCreated={() => {
            setShowAddTenant(false);
            queryClient.invalidateQueries({ queryKey: ["sa-overview"] });
          }}
        />
      )}
    </div>
  );
}

// ── Tenant summary card ──
function TenantCard({ tenant, color, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="text-left rounded-2xl p-5 bg-white shadow-sm border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-extrabold"
            style={{ background: color }}
          >
            {tenant.name.charAt(0)}
          </div>
          <div>
            <h3 className="font-bold text-slate-800 leading-tight">{tenant.name}</h3>
            <p className="text-xs text-slate-400">{tenant.industry || "N/A"}</p>
          </div>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-500">
          {tenant.plan}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <p className="text-lg font-extrabold text-slate-800">{tenant.user_count}</p>
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Users</p>
        </div>
        <div>
          <p className="text-lg font-extrabold text-teal-600">{tenant.admin_count}</p>
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Admins</p>
        </div>
        <div>
          <p className="text-lg font-extrabold text-indigo-600">{tenant.manager_count}</p>
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Mgrs</p>
        </div>
        <div>
          <p className="text-lg font-extrabold text-slate-600">{tenant.employee_count}</p>
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Emp</p>
        </div>
      </div>
      <p className="mt-4 text-xs text-slate-400">Seats: {tenant.seats} · {tenant.domain}</p>
    </button>
  );
}

// ── Tenant detail: users table + add user ──
function TenantDetail({ tenantId, onBack }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showAddUser, setShowAddUser] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["sa-tenant", tenantId],
    queryFn: () => backendApi.superadmin.getTenant(tenantId),
  });

  // Superadmin can no longer delete a client's user directly: this raises a
  // pending request that the client's own admin must approve before the
  // user is actually deleted. Rejecting leaves the user untouched.
  const requestDeleteUser = useMutation({
    mutationFn: (userId) => backendApi.superadmin.requestUserDeletion(tenantId, userId),
    onSuccess: () => {
      toast.success("Removal request sent, waiting on the client admin's approval");
      queryClient.invalidateQueries({ queryKey: ["sa-tenant", tenantId] });
    },
    onError: (e) => toast.error(e.message || "Could not send removal request"),
  });

  const tenant = data?.tenant;
  const users = (data?.users || []).filter(
    (u) =>
      u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.department?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to all clients
      </button>

      {isLoading ? (
        <p className="text-slate-400">Loading…</p>
      ) : (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-800">{tenant?.name}</h2>
              <p className="text-slate-500 text-sm">
                {tenant?.industry} · {tenant?.user_count} users · {tenant?.admin_count} admin · {tenant?.manager_count} managers · {tenant?.employee_count} employees
              </p>
            </div>
            <button
              onClick={() => setShowAddUser(true)}
              className="h-11 px-5 rounded-xl text-sm font-bold inline-flex items-center gap-2 text-white bg-teal-600 hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" /> Add User
            </button>
          </div>

          <div className="relative mb-4 max-w-sm">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users…"
              className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>

          <div className="rounded-2xl bg-white shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Employee</th>
                  <th className="px-4 py-3 font-semibold">ID</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Department</th>
                  <th className="px-4 py-3 font-semibold">Reports To</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white font-bold text-xs">
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{u.full_name}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{u.employee_id}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded-full ${roleBadge(u.role)}`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.position || u.department || "N/A"}</td>
                    <td className="px-4 py-3 text-slate-600">{u.reporting_manager_name || "N/A"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold ${u.status === "Active" ? "text-green-600" : "text-amber-600"}`}>{u.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.pending_deletion_request ? (
                        <span className="text-[11px] font-bold uppercase text-amber-600" title="Waiting on the client admin's approval">
                          Pending approval
                        </span>
                      ) : (
                        <button
                          onClick={() =>
                            window.confirm(
                              `Send a removal request for ${u.full_name}? The client's admin will need to approve it before the user is deleted.`
                            ) && requestDeleteUser.mutate(u.id)
                          }
                          disabled={requestDeleteUser.isLoading}
                          className="text-slate-300 hover:text-red-500 disabled:opacity-50"
                          title="Request removal"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showAddUser && (
        <AddUserModal
          tenantId={tenantId}
          users={data?.users || []}
          onClose={() => setShowAddUser(false)}
          onCreated={() => {
            setShowAddUser(false);
            queryClient.invalidateQueries({ queryKey: ["sa-tenant", tenantId] });
            queryClient.invalidateQueries({ queryKey: ["sa-overview"] });
          }}
        />
      )}
    </div>
  );
}

// ── Reusable modal shell + field ──
function ModalShell({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-extrabold text-slate-800">{title}</h3>
            {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const inputCls =
  "w-full h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30";

// ── Onboard a new client (tenant) ──
function AddTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", industry: "", domain: "", plan: "HRMS", seats: 25,
    admin_first_name: "", admin_last_name: "", admin_email: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const mutation = useMutation({
    mutationFn: (data) => backendApi.superadmin.createTenant(data),
    onSuccess: () => {
      toast.success("Client onboarded");
      onCreated();
    },
    onError: (e) => toast.error(e.message || "Could not create client"),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Company name is required");
    mutation.mutate(form);
  };

  return (
    <ModalShell title="Onboard New Client" subtitle="Create a tenant and its first admin" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Company Name"><input className={inputCls} value={form.name} onChange={set("name")} /></Field>
          <Field label="Industry"><input className={inputCls} value={form.industry} onChange={set("industry")} /></Field>
          <Field label="Domain"><input className={inputCls} placeholder="acme.com" value={form.domain} onChange={set("domain")} /></Field>
          <Field label="Seats"><input type="number" className={inputCls} value={form.seats} onChange={set("seats")} /></Field>
        </div>

        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Company Admin (optional)</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name"><input className={inputCls} value={form.admin_first_name} onChange={set("admin_first_name")} /></Field>
            <Field label="Last Name"><input className={inputCls} value={form.admin_last_name} onChange={set("admin_last_name")} /></Field>
          </div>
          <div className="mt-4">
            <Field label="Admin Email"><input className={inputCls} type="email" value={form.admin_email} onChange={set("admin_email")} /></Field>
          </div>
          <p className="text-xs text-slate-400 mt-2">Default password: <b>Welcome@123</b></p>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 font-semibold text-slate-600">Cancel</button>
          <button type="submit" disabled={mutation.isLoading} className="flex-1 h-11 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold disabled:opacity-60">
            {mutation.isLoading ? "Creating…" : "Create Client"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Add a user to a tenant ──
function AddUserModal({ tenantId, users, onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    role: "employee", department: "", position: "", reporting_manager: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Only admins & managers can be selected as reporting managers.
  const managers = users.filter((u) => ["admin", "manager", "hr"].includes(u.role));

  const mutation = useMutation({
    mutationFn: (data) => backendApi.superadmin.addUser(tenantId, data),
    onSuccess: () => {
      toast.success("User created");
      onCreated();
    },
    onError: (e) => toast.error(e.message || "Could not create user"),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) return toast.error("First name and email are required");
    mutation.mutate(form);
  };

  return (
    <ModalShell title="Add User" subtitle="Provision a new account in this client" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="First Name"><input className={inputCls} value={form.first_name} onChange={set("first_name")} /></Field>
          <Field label="Last Name"><input className={inputCls} value={form.last_name} onChange={set("last_name")} /></Field>
        </div>
        <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone"><input className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
          <Field label="Role">
            <select className={inputCls} value={form.role} onChange={set("role")}>
              <option value="employee">Employee</option>
              <option value="manager">Reporting Manager</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Department"><input className={inputCls} value={form.department} onChange={set("department")} /></Field>
          <Field label="Position"><input className={inputCls} value={form.position} onChange={set("position")} /></Field>
        </div>
        <Field label="Reports To">
          <select className={inputCls} value={form.reporting_manager} onChange={set("reporting_manager")}>
            <option value="">None</option>
            {managers.map((m) => (
              <option key={m.id} value={m.employee_id}>{m.full_name} ({m.position || m.role})</option>
            ))}
          </select>
        </Field>
        <p className="text-xs text-slate-400">Default password: <b>Welcome@123</b></p>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 font-semibold text-slate-600">Cancel</button>
          <button type="submit" disabled={mutation.isLoading} className="flex-1 h-11 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold disabled:opacity-60">
            {mutation.isLoading ? "Creating…" : "Create User"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
